import { TestModule } from '#src/extractTestsFromModule';
import { TestParserOptions } from '../options/testParserOptions.js';
import { Test, TestCollection } from '../parser/models/all.js';
import { PostProcessContext } from './models/postProcessContext.js';
import * as TestProcessors from './post/all.js';

const postProcessorOrder = [
  "processHasOnly",
  "processClassInstTestCases",
  "processTestCases",
];

/**
 * A function that further refines or expands a discovered test.
 * @param test - The test to process.
 * @param index - The index of the test in the pre-processed collection.
 * @param context - The post-processing context.
 * @returns True if the test was handled and should not be automatically added to the final collection.
 */
export type PostProcessor = (
  test: Test,
  index: number,
  context: PostProcessContext
) => boolean;

const postProcessors: PostProcessor[] = postProcessorOrder.map(
  pk => (TestProcessors as any)[pk]
);

/**
 * Executes the post-processing phase of the test parsing pipeline.
 * Handles logic like parameterized test expansion and 'only' filter application.
 * 
 * @param moduleToParse - The original module containing the tests.
 * @param preTests - The collection of tests from the pre-processing phase.
 * @param options - Parser configuration options.
 * @returns A {@link TestCollection} containing the final, fully processed tests.
 */
export function executePostProcessing(moduleToParse: TestModule, preTests: TestCollection, options: TestParserOptions): TestCollection {
  const postTests = new TestCollection();

  const postProcessContext = new PostProcessContext(
    moduleToParse,
    options,
    preTests,
    postTests
  );

  preTests.forEach((test, testIndex) => {
    for (let index = 0; index < postProcessors.length; index++) {
      const processor = postProcessors[index];
      const processResult = processor(test, testIndex, postProcessContext);
      if (processResult === true) return;
    }

    postTests.push(test);
  });

  return postTests;
}
